/**
 * Store-to-map sync subscriptions.
 * Listens to Zustand store changes and updates the map accordingly.
 */

import { useEffect } from 'react';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { updateContacts, animateCotUpdate } from '@/map/markers';
import { updateTrackHistory } from '@/map/track-history';
import { applyStyle2D, applyStyle3D } from '@/map/map-styles';
import { applyLabelMode } from '@/map/map-setup';
import { frame2D, frame3D } from '@/map/coverage/footprint-camera';
import { useContactsStore } from '@/stores/contacts-store';
import { useCotStore } from '@/stores/cot-store';
import { useMapStore } from '@/stores/map-store';
import { useH3Store } from '@/stores/h3-store';
import { snapEvents } from '@/map/h3/h3-utils';
import { DEFAULT_CENTER, DEFAULT_ZOOM, DEFAULT_3D_RANGE } from '@/config';

/** Subscribe store changes to map updates */
export function useMapSubscriptions(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  useEffect(() => {
    const unsubContacts = subscribeContacts(mapRef, modeRef);
    const unsubCot = subscribeCot(mapRef, modeRef);
    const unsubFlyTo = subscribeFlyTo(mapRef, modeRef);
    const unsubStyle = subscribeStyle(mapRef, modeRef);
    const unsubReset = subscribeReset(mapRef, modeRef);
    const unsubLabels = subscribeLabels(mapRef, modeRef);

    return () => {
      unsubContacts();
      unsubCot();
      unsubFlyTo();
      unsubStyle();
      unsubReset();
      unsubLabels();
    };
  }, []);
}

/** Subscribe to contacts changes and update map markers */
function subscribeContacts(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  let prev = useContactsStore.getState().contacts;
  return useContactsStore.subscribe((state) => {
    if (state.contacts !== prev && mapRef.current) {
      prev = state.contacts;
      updateContacts(mapRef.current, modeRef.current, state.contacts);
    }
  });
}

/** Subscribe to CoT events and animate updates + track history */
function subscribeCot(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  let prev = useCotStore.getState().events;
  return useCotStore.subscribe((state) => {
    if (state.events !== prev && mapRef.current) {
      prev = state.events;
      const map = mapRef.current;
      const mode = modeRef.current;
      const { snapUnits, resolution } = useH3Store.getState();
      const events = snapUnits ? snapEvents(state.events, resolution) : state.events;
      animateCotUpdate(map, mode, events, state.previousPositions);
      updateTrackHistory(map, mode, events);
      useCotStore.getState().updatePreviousPositions(events);
    }
  });
}

/** Subscribe to flyTo requests and animate camera */
function subscribeFlyTo(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  return useMapStore.subscribe((state) => {
    const target = state.flyToTarget;
    if (!target || !mapRef.current) return;
    const map = mapRef.current;
    const mode = modeRef.current;

    if ('frame' in target) {
      if (mode === '2d') frame2D(map as google.maps.Map, target.frame);
      else frame3D(map as google.maps.maps3d.Map3DElement, target.frame);
    } else if (mode === '2d') {
      flyTo2D(map as google.maps.Map, target.lng, target.lat, target.zoom);
    } else {
      flyTo3D(map as google.maps.maps3d.Map3DElement, target.lng, target.lat, target.zoom);
    }

    useMapStore.getState().clearFlyTo();
  });
}

/** Subscribe to style changes and update map styling */
function subscribeStyle(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  let prev = useMapStore.getState().activeStyleId;
  return useMapStore.subscribe((state) => {
    if (state.activeStyleId !== prev && mapRef.current) {
      prev = state.activeStyleId;
      const mode = modeRef.current;
      if (mode === '2d') {
        applyStyle2D(mapRef.current as google.maps.Map, state.activeStyleId);
      } else {
        applyStyle3D(mapRef.current as google.maps.maps3d.Map3DElement, state.activeStyleId);
      }
    }
  });
}

/** Subscribe to reset-view requests and restore the default overhead camera. */
function subscribeReset(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  let prev = useMapStore.getState().resetNonce;
  return useMapStore.subscribe((state) => {
    if (state.resetNonce === prev || !mapRef.current) return;
    prev = state.resetNonce;
    if (modeRef.current === '2d') {
      resetView2D(mapRef.current as google.maps.Map);
    } else {
      resetView3D(mapRef.current as google.maps.maps3d.Map3DElement);
    }
  });
}

/** Subscribe to label-visibility changes and flip the 3D map mode live. */
function subscribeLabels(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  let prev = useMapStore.getState().labelsVisible;
  return useMapStore.subscribe((state) => {
    if (state.labelsVisible === prev || !mapRef.current) return;
    prev = state.labelsVisible;
    if (modeRef.current === '3d') {
      applyLabelMode(mapRef.current as google.maps.maps3d.Map3DElement, state.labelsVisible);
    }
  });
}

/** Restore the default 2D overhead view. */
function resetView2D(map: google.maps.Map): void {
  map.panTo(DEFAULT_CENTER);
  map.setZoom(DEFAULT_ZOOM);
}

/** Restore the default 3D overhead globe view. */
function resetView3D(map3d: google.maps.maps3d.Map3DElement): void {
  map3d.flyCameraTo({
    endCamera: {
      center: { lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng, altitude: 0 },
      range: DEFAULT_3D_RANGE,
      tilt: 0,
      heading: 0,
    },
    durationMillis: 1200,
  });
}

/** Fly to a position on 2D map */
function flyTo2D(map: google.maps.Map, lng: number, lat: number, zoom?: number): void {
  map.panTo({ lat, lng });
  if (zoom) {
    map.setZoom(zoom);
  }
}

/** Fly to a position on 3D map, tilting for photorealistic depth when close in. */
function flyTo3D(
  map3d: google.maps.maps3d.Map3DElement,
  lng: number,
  lat: number,
  zoom?: number,
): void {
  const z = zoom ?? 14;
  map3d.flyCameraTo({
    endCamera: {
      center: { lat, lng, altitude: 0 },
      range: zoomToRange(z),
      tilt: tiltForZoom(z),
      heading: 0,
    },
    durationMillis: 1500,
  });
}

/** Camera tilt by zoom — steeper (more oblique 3D depth) the closer you go. */
function tiltForZoom(zoom: number): number {
  if (zoom >= 16) return 67;
  if (zoom >= 13) return 60;
  return 45;
}

/** Convert a 2D zoom level to approximate 3D camera range in meters */
function zoomToRange(zoom: number): number {
  const BASE_RANGE = 40_075_016;
  return BASE_RANGE / Math.pow(2, zoom);
}
